import config from 'config'
import Cookies from 'js-cookie'
import {
  ADS_ENABLED_COOKIE,
  MARKETING_GROUP_ID,
  applyConsent,
  consentedGroupIds,
  hasConsentChoice,
  notifyConsentUpdated,
  purgeRevokedStorage,
  removeAdsEnabledCookie,
  storeConsentChoice,
  updateConsent
} from '../helpers'

export const CookieUserStorage = {
  methods: {
    /**
     * @return {Promise<void>}
     */
    async applyStoredConsent (): Promise<void> {
      await updateConsent()

      const grantedGroupIds = await consentedGroupIds()

      if (await hasConsentChoice()) {
        this.saveAdsEnabledCookie(grantedGroupIds)
      } else {
        removeAdsEnabledCookie()
      }

      await purgeRevokedStorage(grantedGroupIds)
    },
    /**
     * @param {(String|Number)[]} selectedGroupIds
     * @return {Promise<void>}
     */
    async saveConsentChoice (selectedGroupIds: (string | number)[]): Promise<void> {
      const grantedGroupIds = this.withEssentialGroupIds(selectedGroupIds)
      const previousGroupIds = await consentedGroupIds()

      await storeConsentChoice(grantedGroupIds)

      this.saveAdsEnabledCookie(grantedGroupIds)
      applyConsent(grantedGroupIds)

      await purgeRevokedStorage(grantedGroupIds)

      if (this.isWithdrawal(previousGroupIds, grantedGroupIds)) {
        this.reloadToStopRunningTrackers()

        return
      }

      notifyConsentUpdated()
    },
    /**
     * @param {(String|Number)[]} selectedGroupIds
     * @return {Number[]}
     */
    withEssentialGroupIds (selectedGroupIds: (string | number)[]): number[] {
      return [...new Set([
        ...this.getEssentialsCookieGroupIds(),
        ...selectedGroupIds
      ].map(Number))]
    },
    /**
     * @param {Number[]} previousGroupIds
     * @param {Number[]} grantedGroupIds
     * @return {Boolean}
     */
    isWithdrawal (previousGroupIds: number[], grantedGroupIds: number[]): boolean {
      return previousGroupIds.some(groupId => !grantedGroupIds.includes(groupId))
    },
    /**
     * @return {void}
     */
    reloadToStopRunningTrackers (): void {
      window.location.reload()
    },
    /**
     * @param {Number[]} grantedGroupIds
     * @return {void}
     */
    saveAdsEnabledCookie (grantedGroupIds: number[]): void {
      Cookies.set(ADS_ENABLED_COOKIE, grantedGroupIds.includes(MARKETING_GROUP_ID))
    },
    /**
     * @return {Number[]}
     */
    getAllCookieGroupIds () {
      return this.getCookieSetupData.map(({ groupId }) => groupId)
    },
    /**
     * @return {Number[]}
     */
    getEssentialsCookieGroupIds () {
      return this.getCookieSetupData.filter(item => item.isEssential === true).map(({ groupId }) => groupId)
    },
    /**
     * @return {Number[]}
     */
    getCheckedCookieGroupIds () {
      return this.getCookieSetupData.filter(item => item.checked === true).map(({ groupId }) => groupId)
    },
    /**
     * @return {Number[]}
     */
    getConfigDefaultGroupIds () {
      return this.getCookieSetupData.filter(cookie => config?.gdprCookieSetup?.defaultCookies?.includes(Number(cookie.groupId))).map(({ groupId }) => groupId)
    },
    /**
     * @return {Number[]}
     */
    getDefaultCheckedGroupIds () {
      return [...new Set([...this.getEssentialsCookieGroupIds(), ...this.getCheckedCookieGroupIds(), ...this.getConfigDefaultGroupIds()])]
    }
  }
}
